import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  COACH_BRIEF_MISSING_TEXT,
  COACH_REGISTERED_SYMPTOMS_TEXT,
  type CoachBriefLine,
  type CoachSummary,
} from "@care-by-ella/contracts";
import { ForbiddenError, UnauthorizedError } from "@/lib/clinician-api";
import { fetchCoachSummary } from "@/lib/coach-now-api";
import {
  F01_ORIGINALS_ANCHOR,
  memoryItemAnchor,
} from "@/lib/coach-brief-anchors";
import {
  alsoWaitingLine,
  handoffLines,
  mainHelpText,
  nextStepOf,
} from "@/lib/coach-summary";

// TES-1101 (Ö-320–323) — the coach's fast top summary, directly under the
// persistent header. A-01: "Direkt under patientens namn visas en kompakt,
// alltid synlig översikt …"
//
// The header above already carries identity, weight, height, BMI, goal
// weight, phase, treatment month, plan and coach access; this card adds what
// she mainly wants help with, goals and needs, the next coach task, the open
// handoffs and the neutral medical status, so the coach does not have to read
// the cards further down to know who she is meeting. Every line links to
// where it comes from: a Patientminnet row (and F-01's original answer), the
// chat, the check-ins or the handoffs.
//
// `refreshKey` follows the page's own reloads and Patientminnet's writes.
export function CoachTopSummary({
  patientId,
  refreshKey,
  onUnauthorized,
}: {
  patientId: string;
  refreshKey: number;
  onUnauthorized?: () => void;
}) {
  const [summary, setSummary] = useState<CoachSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  // In a ref: the page passes this inline.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchCoachSummary(patientId)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current?.();
          return;
        }
        if (e instanceof ForbiddenError) {
          setForbidden(true);
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, refreshKey]);

  if (forbidden) return null;

  return (
    <section
      aria-labelledby="coach-oversikt"
      className="rounded-lg border border-surfaceMuted bg-surface p-md flex flex-col gap-md lg:px-lg"
    >
      <div>
        <h2 id="coach-oversikt" className="font-display text-lg">
          Översikt
        </h2>
        <p className="text-xs text-textSecondary">
          Det viktigaste om patienten, med källa och datum. Saknas något står
          det. Hela bilden finns under Patientbild.
        </p>
      </div>

      {error ? (
        <p className="text-sm text-danger">Kunde inte hämta: {error}</p>
      ) : !summary ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : (
        <>
          {summary.medicalStatus ? (
            <p className="rounded-md border border-surfaceMuted bg-surfaceMuted/30 p-sm text-sm text-textPrimary">
              {summary.medicalStatus}
            </p>
          ) : null}
          {/* TES-1448 (Ö-709): verbatim, no details and no link. */}
          {summary.registeredSymptoms ? (
            <p className="rounded-md border border-surfaceMuted bg-surfaceMuted/30 p-sm text-sm text-textPrimary">
              {COACH_REGISTERED_SYMPTOMS_TEXT}
            </p>
          ) : null}
          <div className="grid gap-md sm:grid-cols-2 xl:grid-cols-4">
            <Block title="Vill främst ha hjälp med">
              {summary.mainHelp.length === 0 ? (
                <Missing />
              ) : (
                summary.mainHelp.map((line) => (
                  <Line
                    key={line.itemId}
                    line={line}
                    text={mainHelpText(line.body)}
                  />
                ))
              )}
            </Block>
            <Block title="Mål och behov">
              {summary.goalsAndNeeds.lines.length === 0 ? (
                <Missing />
              ) : (
                summary.goalsAndNeeds.lines.map((line) => (
                  <Line key={line.itemId} line={line} text={line.body} />
                ))
              )}
              {summary.goalsAndNeeds.more > 0 ? (
                <a
                  href="#patienten-i-korthet"
                  className="text-xs text-brand hover:underline"
                >
                  {summary.goalsAndNeeds.more} till i Patienten i korthet
                </a>
              ) : null}
            </Block>
            <NextStep summary={summary} />
            <Block title="Överlämningar">
              {handoffLines(summary).map((l) => (
                <p key={l} className="text-sm">
                  {l}
                </p>
              ))}
              {summary.handoffs.toCoach.open + summary.handoffs.fromCoach.open >
              0 ? (
                <a
                  href="#handoff"
                  className="text-xs text-brand hover:underline"
                >
                  Öppna överlämningarna
                </a>
              ) : null}
            </Block>
          </div>
        </>
      )}
    </section>
  );
}

function NextStep({ summary }: { summary: CoachSummary }) {
  const step = nextStepOf(summary);
  const also = alsoWaitingLine(summary);
  return (
    <Block title="Nästa coachuppgift">
      <p className="text-sm text-textPrimary">
        {step.href ? (
          <a href={step.href} className="text-brand hover:underline">
            {step.text}
          </a>
        ) : (
          step.text
        )}
      </p>
      {step.detail ? (
        <p className="text-xs text-textSecondary">{step.detail}</p>
      ) : null}
      {also ? <p className="text-xs text-textSecondary">{also}</p> : null}
    </Block>
  );
}

function Line({ line, text }: { line: CoachBriefLine; text: string }) {
  return (
    <p className="mb-xs text-sm">
      <span className="text-textPrimary">{text}</span>
      <span className="block text-xs text-textSecondary">
        <a
          href={`#${memoryItemAnchor(line.itemId)}`}
          className="text-brand hover:underline"
        >
          {line.sourceLabel} · {fmtDate(line.observedAt)}
        </a>
        {line.fromF01 ? (
          <>
            {" · "}
            <a
              href={`#${F01_ORIGINALS_ANCHOR}`}
              className="text-brand hover:underline"
            >
              Originalsvar
            </a>
          </>
        ) : null}
      </span>
    </p>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function Missing() {
  return (
    <p className="text-sm text-textSecondary">{COACH_BRIEF_MISSING_TEXT}</p>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <h3 className="mb-xs text-xs font-semibold uppercase tracking-wide text-textSecondary">
        {title}
      </h3>
      {children}
    </div>
  );
}
